/**
 * 硬条件匹配规则引擎
 * 基于学校层次、专业对口度、年龄、应届生身份等做加权评分
 */

// 学校层次权重配置
const SCHOOL_TIERS = {
  '985': { score: 100, label: '985高校' },
  '211': { score: 85, label: '211高校' },
  '双一流': { score: 80, label: '双一流高校' },
  '一本': { score: 65, label: '一本院校' },
  '二本': { score: 50, label: '二本院校' },
  '专科': { score: 30, label: '专科院校' }
}

// 政治面貌权重
const POLITICAL_STATUS = {
  '党员': { score: 100, label: '中共党员' },
  '预备党员': { score: 95, label: '预备党员' },
  '团员': { score: 70, label: '共青团员' },
  '群众': { score: 50, label: '群众' }
}

// 专业对口度（简化版，实际应配置更详细的专业映射）
const MAJOR_MATCH = {
  '计算机类': { score: 90, categories: ['计算机科学与技术', '软件工程', '信息安全', '网络工程'] },
  '经管类': { score: 85, categories: ['工商管理', '会计学', '金融学', '经济学'] },
  '工程类': { score: 80, categories: ['土木工程', '机械工程', '电气工程', '自动化'] },
  '文科类': { score: 60, categories: ['汉语言文学', '新闻传播', '法学', '英语'] },
  '其他': { score: 50, categories: [] }
}

// 年龄评分（央国企一般偏好25岁以下应届生）
function calculateAgeScore(age) {
  if (age <= 22) return 100
  if (age <= 24) return 90
  if (age <= 26) return 75
  if (age <= 28) return 60
  return 40
}

// 应届生身份评分
function calculateIdentityScore(identity) {
  const scores = {
    '应届生': 100,
    '择业期应届生': 85,
    '往届生': 50
  }
  return scores[identity] || 50
}

/**
 * 计算硬条件匹配分数
 * @param {Object} data - 用户填写的硬条件数据
 * @returns {Object} - 匹配结果
 */
export function calculateMatchScore(data) {
  const {
    school,
    major,
    age,
    politicalStatus,
    identity,
    gender
  } = data

  // 各项得分
  const schoolScore = SCHOOL_TIERS[school]?.score || 50
  const majorScore = getMajorScore(major)
  const ageScore = calculateAgeScore(parseInt(age))
  const politicalScore = POLITICAL_STATUS[politicalStatus]?.score || 50
  const identityScore = calculateIdentityScore(identity)
  const genderScore = 80 // 性别一般不直接影响，设为基准分

  // 权重配置（总和100%）
  const weights = {
    school: 0.25,
    major: 0.20,
    age: 0.15,
    political: 0.15,
    identity: 0.20,
    gender: 0.05
  }

  // 加权计算总分
  const totalScore = Math.round(
    schoolScore * weights.school +
    majorScore * weights.major +
    ageScore * weights.age +
    politicalScore * weights.political +
    identityScore * weights.identity +
    genderScore * weights.gender
  )

  // 确定分档
  let tier, advice, insight
  
  if (totalScore >= 86) {
    tier = 'A+'
    advice = '条件优秀，有机会冲击核心岗位'
    insight = generateInsight(data, 'excellent', totalScore)
  } else if (totalScore >= 75) {
    tier = 'A'
    advice = '做好准备，有机会冲击央国企总部'
    insight = generateInsight(data, 'high', totalScore)
  } else if (totalScore >= 60) {
    tier = 'B'
    advice = '建议冲央国企二级公司'
    insight = generateInsight(data, 'medium', totalScore)
  } else if (totalScore >= 45) {
    tier = 'C'
    advice = '建议冲央国企三级公司'
    insight = generateInsight(data, 'low', totalScore)
  } else {
    tier = 'D'
    advice = '需整体优化，要有思想准备'
    insight = generateInsight(data, 'very_low', totalScore)
  }

  return {
    score: totalScore,
    tier,
    advice,
    insight,
    details: {
      school: { score: schoolScore, weight: weights.school, label: SCHOOL_TIERS[school]?.label || school },
      major: { score: majorScore, weight: weights.major, label: major },
      age: { score: ageScore, weight: weights.age, label: `${age}岁` },
      political: { score: politicalScore, weight: weights.political, label: POLITICAL_STATUS[politicalStatus]?.label || politicalStatus },
      identity: { score: identityScore, weight: weights.identity, label: identity },
      gender: { score: genderScore, weight: weights.gender, label: gender }
    },
    isLowScore: totalScore < 45
  }
}

function getMajorScore(major) {
  for (const [category, info] of Object.entries(MAJOR_MATCH)) {
    if (info.categories.some(m => major.includes(m))) {
      return info.score
    }
  }
  return 50
}

/**
 * 生成专属洞察 - 口语化、情绪共鸣、突出压力
 * @param {Object} data - 用户数据
 * @param {string} level - 分数档位
 * @param {number} totalScore - 总分
 */
function generateInsight(data, level, totalScore) {
  const { school, major, age, politicalStatus, identity } = data
  
  // 86分以上：优秀档，正面鼓励，但提醒竞争激烈
  if (level === 'excellent') {
    const insights = [
      `兄弟可以啊，${school}背景+${major}专业，这配置在央国企圈子里算是"天胡开局"了。但别飘，跟你一样条件的"神仙"今年特别多，总部管培生岗位报录比可能冲到200:1。而且央国企现在卷学历、卷证书、卷实习，你光有硬条件不够，建议早点准备行测申论，再补两段对口实习，别浪费了好底子。`,
      `恭喜，你的硬条件已经打败了全国85%的应届生。但央国企现在卷得很，${school}的${major}专业今年可能扎堆报名——光我们系统里跟你同条件的就有3000+人。同质化严重意味着HR筛简历时根本看不过来，建议突出差异化优势，比如${politicalStatus === '党员' ? '党员身份+学生干部经历' : '实习经历+行业证书'}，不然容易被淹没在简历海里。`,
      `你这条件基本稳了，但系统给你泼盆冷水：央国企招聘早就不是"分数高就能上"的时代了。${major}专业虽然对口，但今年招聘人数可能缩减20%，而报考人数涨了40%。录取比例越来越低，建议你除了总部也看看一级子公司的核心岗，多投几个方向，别死磕一个坑。`
    ]
    return insights[Math.floor(Math.random() * insights.length)]
  }
  
  // 75-85分：良好档，提醒竞争压力
  if (level === 'high') {
    const insights = [
      `${school}背景还行，但${major}专业今年被标了"黄灯警告"——不是不招，是招得少、报得多。系统显示这个专业在央国企的录取比大概是15:1，而且很多岗位明确要求"有相关实习经验"，你没有的话简历关都过不了。你得做好跟一堆"卷王"PK的准备，他们可能有3段实习+2个证书。`,
      `你的条件属于"比上不足比下有余"，${school}在央国企HR眼里算达标，但${age > 24 ? `年龄已经${age}岁了，部分企业卡25岁红线，年龄优势不明显` : '年龄还行，算个加分项'}。问题是跟你同层次学校的人太多了，竞争力不够突出。建议重点冲二级公司和省级分公司，总部管培生大概率是陪跑，别浪费时间。`,
      `说实话，${school}的${major}专业今年特别卷，跟你同条件的预计有5000+人在投。你的优势不明显——学校不是顶尖，专业又不是紧缺方向，求职方向如果不明确的话，投100份可能回10份。建议赶紧补一段相关实习，或者考个行业证书，不然网申阶段就可能被机筛刷掉。`
    ]
    return insights[Math.floor(Math.random() * insights.length)]
  }
  
  // 60-74分：中等档，指出问题和方向
  if (level === 'medium') {
    const insights = [
      `系统给你打了${totalScore}分，直白说就是"能进，但得降低预期"。${school}在央国企体系里属于"边缘认可"，${major}专业又不在核心需求清单上——很多岗位直接写明"985/211优先"，你投过去就是陪跑。录取比例低得吓人，建议重点看三级子公司和地方城投，总部别想了，差距太大。`,
      `你的硬伤比较明显：${school}层次不够硬，${major}专业对口度一般。央国企现在卡学历卡得很死，二本以下简历直接机筛掉，HR根本看不到。而且你专业需求度不高，竞争优势不足。建议曲线救国——先考个在职研究生提升学历，或者从劳务派遣转正的路子走，虽然慢但有机会。`,
      `${totalScore}分意味着你跟央国企核心岗基本无缘了。今年就业形势你也知道，人多岗位少，央国企更是挤破头。但别慌，系统建议你重点关注"地方国企"和"混合所有制企业"，这些门槛相对低，而且干几年可以通过内部竞聘跳到更好的平台。关键是先找个落脚点，别毕业即失业。`
    ]
    return insights[Math.floor(Math.random() * insights.length)]
  }
  
  // 45-59分：较低档，压力拉满
  if (level === 'low') {
    const insights = [
      `系统没直接劝退，但留了句狠话："建议将就业预期调整为非核心岗位"。翻译一下就是——${school}的${major}专业在央国企体系里属于"备胎中的备胎"，投10份简历可能9份石沉大海。而且今年央国企缩招明显，核心岗竞争比可能到50:1，你这种条件连面试机会都难拿到。`,
      `扎心预警：你的条件在央国企招聘里属于"炮灰区"。${school}不是目标院校，${major}又不在紧缺专业目录，HR看简历可能5秒就扔——机筛都过不了。专业不对口+学校没优势，竞争力几乎为零。建议赶紧Plan B，私企、外企、考公都看看，别在央国企这棵树上吊死。`,
      `${totalScore}分，系统委婉地说"建议拓宽就业视野"。说人话就是：央国企这条路对你来说特别窄，${identity === '往届生' ? '往届生身份更是雪上加霜，很多岗位只要当年应届' : '应届生身份是你唯一的优势，但这个优势也就管一年'}。而且你求职方向如果不调整，风险很大——可能毕业几个月都没着落。建议先找个相关行业的工作积累经验，2年后再通过社招试试。`
    ]
    return insights[Math.floor(Math.random() * insights.length)]
  }
  
  // 45分以下：极低档，直击灵魂
  const veryLowInsights = [
    `系统冷冰冰地提示："与央企基础筛选模型匹配度极低"。这意味着什么？意味着你的简历可能在机筛阶段就直接被系统过滤掉了，连被HR看到的机会都没有。今年央国企报名人数又创新高，录取比例低得离谱，你连做炮灰的资格都悬。赶紧想想别的出路吧，别在这浪费时间了。`,
    `兄弟，说句实话，${totalScore}分在央国企求职里属于"地狱难度"。${school}不是985/211，${major}专业又不吃香，年龄还${age}岁……这条件投央国企基本就是送人头。人多岗位少，你这种背景竞争力为零，专业需求度又低，投了也是白投。建议立刻调整求职方向，私企、考编、考研，选一个赶紧冲，别拖到毕业还没着落。`,
    `系统没明说，但潜台词很清楚：别来蹭央国企热度了。你的条件在招聘系统里可能被自动过滤掉，投了也是白投。今年就业形势这么差，央国企更是卷上天，你这种分数连初筛都过不了。与其浪费时间海投，不如花19.9做个精准测评，看看哪些企业真的适合你，别再做无用功了——时间和机会成本也是钱啊。`
  ]
  return veryLowInsights[Math.floor(Math.random() * veryLowInsights.length)]
}

/**
 * 精准匹配评分（19.9元报告用）
 * 包含更多维度的评估
 */
export function calculatePrecisionScore(data) {
  const baseScore = calculateMatchScore(data)
  
  // 额外维度评分
  const practiceScore = data.practiceExperience ? Math.min(data.practiceExperience.length * 15, 100) : 50
  const clubScore = data.clubActivities ? Math.min(data.clubActivities.length * 20, 100) : 50
  const familyScore = data.familyBackground ? 70 : 50
  const locationScore = data.location ? 80 : 60
  
  const weights = {
    base: 0.50,
    practice: 0.15,
    club: 0.10,
    family: 0.10,
    location: 0.10,
    political: 0.05
  }

  const precisionScore = Math.round(
    baseScore.score * weights.base +
    practiceScore * weights.practice +
    clubScore * weights.club +
    familyScore * weights.family +
    locationScore * weights.location +
    (baseScore.details.political.score * weights.political)
  )

  return {
    ...baseScore,
    precisionScore,
    dimensions: {
      hardConditions: { score: baseScore.score, weight: 0.50, label: '硬性条件' },
      practice: { score: practiceScore, weight: 0.15, label: '实践经历' },
      club: { score: clubScore, weight: 0.10, label: '社团活动' },
      family: { score: familyScore, weight: 0.10, label: '家庭背景' },
      location: { score: locationScore, weight: 0.10, label: '地域匹配' },
      political: { score: baseScore.details.political.score, weight: 0.05, label: '政治素养' }
    }
  }
}

export { SCHOOL_TIERS, POLITICAL_STATUS, MAJOR_MATCH }
